import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

/** Provides the sheet's generated title id so SheetTitle can render it. */
const SheetTitleContext = createContext<string | undefined>(undefined);

export function Sheet({ open, onClose, children, className }: SheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  // Keep the latest onClose available to the keydown handler without
  // forcing the lifecycle effect below to depend on it.
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key === "Tab" && contentRef.current) {
        const focusable = getFocusableElements(contentRef.current);
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
          if (active === first || !contentRef.current.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    if (open) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";

      // Move focus into the sheet after mount: prefer the first focusable
      // element, falling back to the content container itself.
      const raf = requestAnimationFrame(() => {
        if (!contentRef.current) return;
        const focusable = getFocusableElements(contentRef.current);
        if (focusable.length > 0) {
          focusable[0].focus();
        } else {
          contentRef.current.focus();
        }
      });

      return () => {
        cancelAnimationFrame(raf);
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "";
        previouslyFocusedRef.current?.focus();
      };
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
    // onClose is intentionally excluded: it's read via onCloseRef so a new
    // onClose identity from a parent re-render doesn't re-capture focus or
    // re-run the initial-focus RAF while the sheet stays open.
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 bg-black/40"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "fixed right-0 top-0 h-full w-full max-w-lg overflow-y-auto border-l border-border bg-white p-6 shadow-lg",
          "focus:outline-none",
          className
        )}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-md p-1 text-text-muted hover:bg-sidebar hover:text-text transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <SheetTitleContext.Provider value={titleId}>
          {children}
        </SheetTitleContext.Provider>
      </div>
    </div>
  );
}

export function SheetHeader({ children }: { children: ReactNode }) {
  return <div className="mb-6 pr-8">{children}</div>;
}

export function SheetTitle({ children, className }: { children: ReactNode; className?: string }) {
  const titleId = useContext(SheetTitleContext);
  return (
    <h2 id={titleId} className={cn("font-heading text-xl font-bold text-text", className)}>{children}</h2>
  );
}

export function SheetDescription({ children }: { children: ReactNode }) {
  return <p className="mt-1 font-body text-sm text-text-muted">{children}</p>;
}

export function SheetFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mt-8 flex items-center gap-3 border-t border-border pt-6",
        className
      )}
    >
      {children}
    </div>
  );
}
